import { z } from "zod";
import type { BoulevardReceiptView } from "./boulevard";

/**
 * Past visits from the old system (Boulevard's "Appointment line items" report): read-only history.
 * The browser reads the file and groups its rows by appointment; the server matches clients.
 */
export const BOULEVARD_VISIT_COLUMNS = {
  ref: "Appointment ID",
  itemRef: "Appointment service ID",
  state: "Appointment state",
  itemName: "Appointment service name",
  start: "Service start time",
  end: "Service end time",
  amount: "Final net sale amount",
  addOn: "Is service add on",
  discount: "Discount name",
  category: "Service category",
  clientRef: "Client ID",
  clientName: "Client name",
  email: "Client email",
  mobile: "Mobile phone",
  nurse: "Service provider name",
  date: "Appointment date",
} as const;

const time = z
  .string()
  .regex(/^\d{2}:\d{2}$/)
  .nullable();

export const pastVisitItemSchema = z.object({
  name: z.string().trim().min(1).max(200),
  category: z.string().trim().max(200).default(""),
  addOn: z.boolean(),
  amountCents: z.number().int().min(-10_000_000).max(10_000_000),
});
export type PastVisitItem = z.infer<typeof pastVisitItemSchema>;

export const pastVisitInputSchema = z.object({
  ref: z.string().trim().min(1).max(100),
  clientRef: z.string().trim().max(100).nullable(),
  clientName: z.string().trim().max(200).default(""),
  email: z.string().trim().max(320).nullable(),
  mobile: z.string().trim().max(40).nullable(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  start: time,
  end: time,
  nurse: z.string().trim().max(200).default(""),
  status: z.string().trim().max(50).default(""),
  discount: z.string().trim().max(300).default(""),
  totalCents: z.number().int().min(-10_000_000).max(10_000_000),
  items: z.array(pastVisitItemSchema).max(50),
});
export type PastVisitInput = z.infer<typeof pastVisitInputSchema>;

export const importPastVisitsSchema = z.object({ visits: z.array(pastVisitInputSchema).min(1, "No visits in the file").max(50_000) });

export interface PastVisitPreview {
  total: number;
  /** Matched to a client: new to Vaulted, or already imported (updated). */
  create: number;
  update: number;
  items: number;
  totalCents: number;
  /** Not closed out in the old system (state isn't "Final"). */
  notFinal: { date: string; status: string; items: string }[];
  /** No client to put them on, and why. */
  unmatched: { date: string; name: string; reason: string }[];
  /** How they were matched: by the old system's client ID, email, or mobile. */
  matchedBy: { ref: number; email: number; mobile: number };
}

export interface PastVisitResult {
  created: number;
  updated: number;
  unmatched: number;
}

/** A past visit on the client's Visits tab. */
export interface PastVisitView {
  id: string;
  source: string;
  /** The old system's appointment ID (its forms and charts point to it). */
  ref: string;
  date: string;
  start: string | null;
  end: string | null;
  nurse: string;
  status: string;
  discount: string;
  totalCents: number;
  items: PastVisitItem[];
  /** Its checkout as Boulevard recorded it (tip and payments too), once read. */
  receipt: BoulevardReceiptView | null;
}
